import type { ValidationConfig } from '../../../core/dataProcessor/types';

type TreeNode = Record<string, unknown> & {
  children?: TreeNode[];
};

// 🆕 为新 ValidationEngine 导出的配置
export const drillableTreemapValidationConfig: ValidationConfig = {
  fields: [
    { field: 'x', type: 'string', required: true },
    { field: 'y', type: 'number', required: true },
    { field: 'group', type: ['string', 'date', 'number'], required: true }
  ],
  businessRules: [
    {
      name: 'drillable-treemap-hierarchical-structure',
      validator: (data, layer) => {
        if (!Array.isArray(data)) {
          return true;
        }
        return data.some(
          (item: TreeNode) => Array.isArray(item.children) && item.children.length > 0
        );
      },
      message: 'Drillable TreeMap requires hierarchical data structure',
      suggestion: 'Provide data with "children" property containing nested hierarchical structure',
      severity: 'error'
    },
    {
      name: 'drillable-treemap-positive-values',
      validator: (data, layer) => {
        if (!Array.isArray(data)) {
          return true;
        }
        const { encoding } = layer;
        return data.every((item: TreeNode) => {
          const value = item[encoding?.y as string];
          return typeof value === 'number' && value > 0;
        });
      },
      message: 'Drillable TreeMap values should be positive',
      suggestion:
        'TreeMap areas are based on positive values; ensure all y values are greater than 0',
      severity: 'error'
    },
    {
      name: 'drillable-treemap-category-not-empty',
      validator: (data, layer) => {
        if (!Array.isArray(data)) {
          return true;
        }
        const { encoding } = layer;
        return data.every((item: TreeNode) => {
          const category = item[encoding?.x as string];
          return category && typeof category === 'string' && category.trim().length > 0;
        });
      },
      message: 'Drillable TreeMap categories cannot be empty',
      suggestion: 'Ensure all category names (x field) are non-empty strings',
      severity: 'error'
    },
    {
      name: 'drillable-treemap-sufficient-nodes',
      validator: data => Array.isArray(data) && data.length >= 1,
      message: 'Drillable TreeMap requires at least 1 root node',
      suggestion: 'Provide at least 1 root node for TreeMap visualization',
      severity: 'error'
    },
    {
      name: 'drillable-treemap-hierarchy-depth',
      validator: (data, layer) => {
        if (!Array.isArray(data)) {
          return true;
        }
        const maxDepth = (node: TreeNode, currentDepth = 0): number => {
          if (!node.children || !Array.isArray(node.children) || node.children.length === 0) {
            return currentDepth;
          }

          return Math.max(
            ...node.children.map((child: TreeNode) => maxDepth(child, currentDepth + 1))
          );
        };

        const depths = data.map((item: TreeNode) => maxDepth(item));
        const maxHierarchyDepth = Math.max(...depths);

        return maxHierarchyDepth >= 1 && maxHierarchyDepth <= 5;
      },
      message: 'Drillable TreeMap hierarchy should have 1-5 levels',
      suggestion: 'Design hierarchy with 1-5 levels for optimal drill-down experience',
      severity: 'warning'
    },
    {
      name: 'drillable-treemap-children-consistency',
      validator: (data, layer) => {
        if (!Array.isArray(data)) {
          return true;
        }
        const validateNode = (node: TreeNode): boolean => {
          if (!node.children) {
            return true; // 叶子节点
          }

          if (!Array.isArray(node.children)) {
            return false;
          }

          // 递归检查子节点
          return node.children.every((child: TreeNode) => validateNode(child));
        };

        return data.every((item: TreeNode) => validateNode(item));
      },
      message: 'Drillable TreeMap children property must be arrays or undefined',
      suggestion: 'Ensure all children properties are arrays containing child nodes',
      severity: 'error'
    },
    {
      name: 'drillable-treemap-optimal-root-count',
      validator: data => Array.isArray(data) && data.length <= 20,
      message: 'Drillable TreeMap works best with 20 or fewer root nodes',
      suggestion: 'Consider limiting to 20 root nodes for better visualization and performance',
      severity: 'warning'
    },
    {
      name: 'drillable-treemap-children-count',
      validator: (data, layer) => {
        if (!Array.isArray(data)) {
          return true;
        }
        const checkChildrenCount = (node: TreeNode): boolean => {
          if (!node.children || !Array.isArray(node.children)) {
            return true;
          }

          // 每个节点的子节点不超过50个
          if (node.children.length > 50) {
            return false;
          }

          // 递归检查子节点
          return node.children.every((child: TreeNode) => checkChildrenCount(child));
        };

        return data.every((item: TreeNode) => checkChildrenCount(item));
      },
      message: 'Each node should have 50 or fewer children',
      suggestion: 'Limit each node to 50 children for better readability and performance',
      severity: 'warning'
    },
    {
      name: 'drillable-treemap-leaf-nodes-exist',
      validator: (data, layer) => {
        if (!Array.isArray(data)) {
          return true;
        }
        const hasLeafNodes = (node: TreeNode): boolean => {
          if (!node.children || !Array.isArray(node.children) || node.children.length === 0) {
            return true; // 这是叶子节点
          }

          // 递归检查是否有叶子节点
          return node.children.some((child: TreeNode) => hasLeafNodes(child));
        };

        return data.every((item: TreeNode) => hasLeafNodes(item));
      },
      message: 'Drillable TreeMap should contain leaf nodes',
      suggestion: 'Ensure the hierarchy contains nodes without children (leaf nodes)',
      severity: 'warning'
    }
  ]
};
